import React, { useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { ExternalLink, TrendingUp, Users, Zap } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';
import CaseStudyVisual from './CaseStudyVisual';

const projects = [
  {
    id: 1,
    title: 'AI-Powered Trading Platform',
    client: 'FinanceFlow Inc.',
    category: 'Artificial Intelligence',
    year: '2024',
    tags: ['AI/ML', 'Real-time Analytics', 'Cloud'],
    gradient: 'from-[#00D4FF] via-[#4A3AFF] to-[#7B61FF]',
    accent: '#00D4FF',
    challenge: 'Build a real-time stock prediction system that processes millions of market data points per second with 99.9% accuracy.',
    solution: 'Implemented GPT-4 powered analysis engine with custom ML models for pattern recognition. Deployed on AWS Lambda for auto-scaling.',
    results: [
      { metric: '10x', label: 'Faster Processing', description: 'Reduced analysis time from 10s to 1s' },
      { metric: '50K+', label: 'Active Users', description: 'Growing 200% month-over-month' },
      { metric: '40%', label: 'Cost Reduction', description: 'Optimized cloud infrastructure' },
    ],
    technologies: ['Python', 'TensorFlow', 'GPT-4 API', 'AWS Lambda', 'DynamoDB', 'WebSocket'],
    features: [
      'Real-time market sentiment analysis',
      'Predictive price modeling',
      'Automated trading signals',
      'Risk assessment dashboard',
      'Portfolio optimization'
    ],
    testimonial: {
      quote: 'ABCI transformed our trading platform into an AI powerhouse. The accuracy and speed are game-changing.',
      author: 'Sarah Chen',
      role: 'CTO, FinanceFlow Inc.'
    }
  },
  {
    id: 2,
    title: 'DeFi Lending Protocol',
    client: 'CryptoVault',
    category: 'Blockchain',
    year: '2024',
    tags: ['Blockchain', 'Smart Contracts', 'DeFi'],
    gradient: 'from-[#00D4FF] via-[#00FF88] to-[#00B4D8]',
    accent: '#00FF88',
    challenge: 'Create a secure, decentralized lending platform with automated yield farming and liquidity pools across multiple chains.',
    solution: 'Developed audited smart contracts on Ethereum & Polygon. Built cross-chain bridge with LayerZero. Implemented automated market maker (AMM) logic.',
    results: [
      { metric: '$25M', label: 'Total Value Locked', description: 'TVL reached in first 6 months' },
      { metric: '15K+', label: 'Unique Wallets', description: 'Active users across chains' },
      { metric: '1M+', label: 'Transactions', description: 'Zero security breaches' },
    ],
    technologies: ['Solidity', 'Hardhat', 'Ethers.js', 'The Graph', 'LayerZero', 'React', 'Web3.js'],
    features: [
      'Multi-chain support (Ethereum, Polygon, BSC)',
      'Automated yield optimization',
      'Flash loan protection',
      'Governance token with voting',
      'Real-time APY calculations'
    ],
    testimonial: {
      quote: 'Security and scalability were our top concerns. ABCI delivered a robust solution that exceeded expectations.',
      author: 'Michael Torres',
      role: 'Founder, CryptoVault'
    }
  },
  {
    id: 3,
    title: 'Metaverse VR Experience',
    client: 'GameStudio XR',
    category: 'Game Development',
    year: '2024',
    tags: ['Unity', 'VR/AR', 'Multiplayer', '3D'],
    gradient: 'from-[#7B61FF] via-[#C850C0] to-[#FF6B6B]',
    accent: '#C850C0',
    challenge: 'Design an immersive VR multiplayer game with realistic physics, cross-platform compatibility, and low latency networking.',
    solution: 'Built with Unity using Photon for networking. Implemented custom physics engine and optimized for Quest 2, PSVR2, and Steam VR.',
    results: [
      { metric: '100K+', label: 'Downloads', description: 'First month on Steam & Meta Store' },
      { metric: '25K', label: 'Daily Active Users', description: 'Average session: 45 minutes' },
      { metric: '4.8★', label: 'App Store Rating', description: '5,000+ reviews' },
    ],
    technologies: ['Unity 2023', 'C#', 'Photon Engine', 'Blender', 'Substance Painter', 'FMOD'],
    features: [
      'Cross-platform multiplayer (up to 32 players)',
      'Realistic physics and haptics',
      'Voice chat with spatial audio',
      'NFT avatar integration',
      'Procedural world generation'
    ],
    testimonial: {
      quote: 'The level of immersion and technical polish ABCI achieved is unmatched. Players are spending hours in our world.',
      author: 'Emma Rodriguez',
      role: 'Creative Director, GameStudio XR'
    }
  },
  {
    id: 4,
    title: 'Enterprise Cloud SaaS',
    client: 'StartupFlow',
    category: 'Full-Stack Development',
    year: '2024',
    tags: ['Full-Stack', 'AWS', 'Microservices', 'DevOps'],
    gradient: 'from-[#0077FF] via-[#00D4FF] to-[#00B4D8]',
    accent: '#00D4FF',
    challenge: 'Architect a scalable SaaS platform handling 10M+ requests/day with 99.99% uptime and enterprise-grade security.',
    solution: 'Microservices architecture on AWS EKS with auto-scaling. Implemented Redis caching, PostgreSQL with read replicas, and CDN delivery.',
    results: [
      { metric: '99.99%', label: 'Uptime', description: 'SLA maintained for 12 months' },
      { metric: '200+', label: 'Enterprise Clients', description: 'Including Fortune 500' },
      { metric: '<50ms', label: 'Response Time', description: 'Global average latency' },
    ],
    technologies: ['React', 'Node.js', 'PostgreSQL', 'Redis', 'AWS EKS', 'Docker', 'Terraform'],
    features: [
      'Multi-tenant architecture',
      'Real-time collaboration (WebSocket)',
      'Advanced analytics dashboard',
      'SSO and SAML integration',
      'Automated CI/CD pipeline'
    ],
    testimonial: {
      quote: 'ABCI built us a platform that scales effortlessly. Their DevOps expertise saved us months of development time.',
      author: 'David Kim',
      role: 'VP Engineering, StartupFlow'
    }
  },
  {
    id: 5,
    title: 'AI Shopping Assistant',
    client: 'ShopElite',
    category: 'Mobile & AI',
    year: '2024',
    tags: ['Mobile', 'React Native', 'AI', 'UX Design'],
    gradient: 'from-[#FF8C42] via-[#C850C0] to-[#7B61FF]',
    accent: '#FF8C42',
    challenge: 'Create a mobile shopping app with AR product preview, AI recommendations, and one-tap checkout to maximize conversion.',
    solution: 'Built with React Native and ARKit/ARCore for AR. Integrated GPT-4 for personalized recommendations and Stripe for payments.',
    results: [
      { metric: '+85%', label: 'Conversion Rate', description: 'Compared to previous app' },
      { metric: '50K', label: 'Monthly Orders', description: '$2.5M revenue/month' },
      { metric: '8min', label: 'Avg Session Time', description: '3x industry average' },
    ],
    technologies: ['React Native', 'ARKit', 'ARCore', 'GPT-4 API', 'Stripe', 'Firebase', 'Redux'],
    features: [
      'AR product try-on (furniture, fashion)',
      'AI-powered recommendations',
      'One-tap checkout (Apple/Google Pay)',
      'Voice search and navigation',
      'Social shopping (share & review)'
    ],
    testimonial: {
      quote: 'The AR feature alone increased our conversion by 60%. ABCI truly understands mobile UX.',
      author: 'Lisa Martinez',
      role: 'CEO, ShopElite'
    }
  },
];

export default function CaseStudies() {
  const [active, setActive] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useTransform(mouseY, [-200, 200], [10, -10]);
  const rotateY = useTransform(mouseX, [-200, 200], [-10, 10]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    mouseX.set(e.clientX - centerX);
    mouseY.set(e.clientY - centerY);
  };

  return (
    <section className="relative py-24 md:py-32" style={{ background: 'transparent' }}>
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8">
        <SectionLabel>Featured Work</SectionLabel>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-[clamp(2rem,4vw,3.5rem)] font-bold font-space text-[#F0F0F5] tracking-[-0.02em] mb-4"
        >
          Case Studies
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-[#8A8AA3] text-lg mb-12 md:mb-16 max-w-2xl"
        >
          Real results from real projects. Hover to explore our success stories.
        </motion.p>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Left: Project list */}
          <div className="lg:w-2/5 space-y-2">
            {projects.map((p, i) => (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
                whileHover={{ x: 5 }}
                className={`w-full text-left px-6 py-5 rounded-xl transition-all duration-300 flex items-start justify-between group ${
                  active === i
                    ? 'bg-[rgba(0,212,255,0.08)] border border-[rgba(0,212,255,0.2)]'
                    : 'bg-transparent border border-transparent hover:bg-[rgba(255,255,255,0.03)] hover:border-[rgba(255,255,255,0.05)]'
                }`}
              >
                <div className="flex items-start gap-4 flex-1">
                  <span className={`text-sm font-mono mt-1 ${active === i ? 'text-[#00D4FF]' : 'text-[#4A4A65]'} transition-colors`}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1">
                    <h4 className={`text-lg font-bold font-space mb-1 ${active === i ? 'text-white' : 'text-white'} transition-colors`}>
                      {p.title}
                    </h4>
                    <p className="text-xs text-[#8A8AA3] mb-1">{p.client} • {p.year}</p>
                    <p className="text-xs text-[#00D4FF] mb-2">{p.category}</p>
                    <div className="flex flex-wrap gap-2">
                      {p.tags.map(t => (
                        <span key={t} className="text-xs bg-[rgba(0,212,255,0.08)] text-[#00D4FF] px-2 py-1 rounded-full">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <ExternalLink className={`w-4 h-4 mt-1 ${active === i ? 'text-[#00D4FF]' : 'text-[#4A4A65]'} transition-colors`} />
              </motion.button>
            ))}
          </div>

          {/* Right: Interactive preview */}
          <div className="lg:w-3/5">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                style={{
                  rotateX: isHovering ? rotateX : 0,
                  rotateY: isHovering ? rotateY : 0,
                  transformPerspective: 1000,
                }}
                className="relative"
              >
                {/* Premium per-project graphic */}
                <CaseStudyVisual project={projects[active]} />

                {/* Info cards with detailed metrics */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {projects[active].results.map((result, i) => (
                    <motion.div
                      key={result.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      whileHover={{ y: -5, scale: 1.02 }}
                      className="p-4 rounded-xl glass border border-[rgba(0,212,255,0.1)] backdrop-blur-xl"
                    >
                      <div className="text-3xl font-bold font-space gradient-text mb-1">
                        {result.metric}
                      </div>
                      <div className="text-sm font-semibold text-white mb-1">{result.label}</div>
                      <div className="text-xs text-[#8A8AA3]">{result.description}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
